import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { SegmentService } from 'app/segment/segment.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ProjectService } from 'app/shared/service/project.service';

import { UserTabsHierarchyBased } from './user-tabs/user-tabs-hierarchy-based';
import { userTabsPopulated } from './user-tabs/user-tabs-populated';
import { UserTabs } from './user-tabs/user-tabs.model';
import { learnMoreLinks } from './users-sitemate/identity-cards/learn-more-links';
import { templateLinks } from './users-sitemate/template-links';

@Component({
  selector: 'cc-users-common-page',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, OnDestroy {
  unSubscribe = new Subject<void>();
  teamId: string;
  projectId: string;
  companyId: string;
  dashpivotListTabs: UserTabs[];
  sitemateListTabs: UserTabs[];

  hasInfoPanel = false;
  sitemateLearnMoreLinks = learnMoreLinks;
  sitemateTemplateLinks = templateLinks;
  userTabs = userTabsPopulated;
  learnMoreTitle: string;
  i18NTitles = {};
  learnHowToUseTitle: string;

  constructor(
    protected readonly projectService: ProjectService,
    protected readonly companyService: CompanyService,
    protected readonly errorHandler: ErrorHandler,
    protected readonly segmentService: SegmentService,
    protected readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    protected readonly i18nService: TmpI18NService,
  ) {}

  ngOnInit(): void {
    this.initializeHierarchy();
    this.updatePanelOnRouteChange();
    this.getAllTitles();
    this.learnHowToUseTitle = this.getTitle('learnHowToUse');
  }

  updatePanelOnRouteChange() {
    this.router.events
      .pipe(
        takeUntil(this.unSubscribe),
        filter((e) => e instanceof NavigationEnd),
      )
      .subscribe(() => {
        const { hasInfoPanel } = this.activatedRoute.snapshot.firstChild.data;
        this.hasInfoPanel = !!hasInfoPanel;
      });
  }

  initializeHierarchy(): void {
    const { hierarchy, hasInfoPanel } = this.activatedRoute.snapshot.firstChild.data;
    this.hasInfoPanel = !!hasInfoPanel;
    this.sitemateListTabs = this.getSitemateTabs();

    const initializationModes = [
      {
        condition: (value: AppHierarchy) => value === AppHierarchy.Team,
        action: this.initializeUserTeam.bind(this),
      },
      {
        condition: (value: AppHierarchy) => value === AppHierarchy.Project,
        action: this.initializeUserProject.bind(this),
      },
      {
        condition: (value: AppHierarchy) => value === AppHierarchy.Organisation,
        action: this.initializeUserCompany.bind(this),
      },
    ];

    const initializationMode = initializationModes.find((entry) => entry.condition(hierarchy));
    initializationMode?.action();
  }

  private getSitemateTabs(): UserTabs[] {
    return UserTabsHierarchyBased.sitemate.tabs;
  }

  initializeUserTeam(): void {
    this.dashpivotListTabs = UserTabsHierarchyBased.dashpivot.team.tabs;

    this.activatedRoute.params
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.unSubscribe),
        filter((params) => !!params.teamId),
      )
      .subscribe((params) => {
        this.teamId = params.teamId;
      }, this.errorHandler.handle.bind(this.errorHandler));
  }

  initializeUserProject(): void {
    this.dashpivotListTabs = UserTabsHierarchyBased.dashpivot.project.tabs;

    this.activatedRoute.params
      .pipe(distinctUntilChanged(), takeUntil(this.unSubscribe))
      .subscribe((params) => {
        this.projectId = params.projectId;
      }, this.errorHandler.handle.bind(this.errorHandler));
  }

  initializeUserCompany(): void {
    this.dashpivotListTabs = UserTabsHierarchyBased.dashpivot.company.tabs;

    this.activatedRoute.params
      .pipe(distinctUntilChanged(), takeUntil(this.unSubscribe))
      .subscribe((params) => {
        this.companyId = params.companyId;
      }, this.errorHandler.handle.bind(this.errorHandler));
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  private getTitle(i18nCode: string) {
    return this.i18nService.getMessage(i18nCode);
  }

  private getAllTitles() {
    this.i18NTitles = this.sitemateLearnMoreLinks.reduce((currentI18N, nextI18N) => {
      const i18NTitle = this.getTitle(nextI18N.titleCode);
      currentI18N[nextI18N.titleCode] = i18NTitle;
      return currentI18N;
    }, {});
  }
}
